import { transparentize } from 'polished'
import React, { useMemo } from 'react'
import styled, {
  ThemeProvider as StyledComponentsThemeProvider,
  createGlobalStyle,
  css,
  DefaultTheme
} from 'styled-components'
import { Text, TextProps } from 'rebass'

import { useIsDarkMode } from '../state/user/hooks'
import { AppExperienceProvider } from './appExperience'
import { paletteDeepSpace } from './palettes'
import { Colors, ThemeId } from './styled'
import fonts from './fonts.module.css'

export * from './components'
export { DeepSpaceViewportBackdrop } from './DeepSpaceViewportBackdrop'
export { AppExperienceProvider, useThemeId } from './appExperience'
export type { ThemeId } from './styled'

const MEDIA_WIDTHS = {
  upToExtraSmall: 500,
  upToSmall: 600,
  upToMedium: 960,
  upToLarge: 1280
}

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce(
  (accumulator, size) => {
    ;(accumulator as any)[size] = (a: any, b: any, c: any) => css`
      @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
        ${css(a, b, c)}
      }
    `
    return accumulator
  },
  {}
) as any

export function colors(darkMode: boolean): Colors {
  return paletteDeepSpace(darkMode)
}

export function theme(darkMode: boolean): DefaultTheme {
  const palette = colors(darkMode)
  const panelShadowDeepSpace =
    '0 0 0 1px rgba(30, 41, 59, 0.65), 0 20px 40px rgba(0, 0, 0, 0.5), 0 0 40px rgba(0, 232, 163, 0.04)'

  return {
    ...palette,
    grids: { sm: 8, md: 12, lg: 24 },
    shadow1: darkMode ? '#000' : '#90a2e3',
    layout: {
      shellBodyPaddingTop: '116px',
      shellBodyPaddingTopMobile: '100px',
      shellBodyPaddingMobile: '16px',
      shellFooterMarginTop: '5rem',
      panelMaxWidth: '420px',
      swapPanelMaxWidth: '520px',
      contentMaxWidth: '1140px',
      sectionGapY: '2rem',
      contentPaddingX: '1rem',
      headerBorderBottom: `1px solid ${transparentize(0.35, '#1e293b')}`
    },
    radii: {
      panel: '14px',
      control: '10px',
      cardMd: '14px',
      cardLg: '14px'
    },
    shadows: {
      panel: darkMode ? panelShadowDeepSpace : '0px 0px 1px rgba(0, 0, 0, 0.01)',
      flyout: '0px 0px 1px rgba(0, 0, 0, 0.01)'
    },
    focus: { ring: palette.primary1 },
    themeId: 'deepSpace',
    mediaWidth: mediaWidthTemplates,
    flexColumnNoWrap: css`
      display: flex;
      flex-flow: column nowrap;
    `,
    flexRowNoWrap: css`
      display: flex;
      flex-flow: row nowrap;
    `
  }
}

function ThemeProviderInner({ children }: { children: React.ReactNode }) {
  const darkMode = useIsDarkMode()
  const themeObject = useMemo(() => theme(darkMode), [darkMode])
  return <StyledComponentsThemeProvider theme={themeObject}>{children}</StyledComponentsThemeProvider>
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    <AppExperienceProvider>
      <ThemeProviderInner>{children}</ThemeProviderInner>
    </AppExperienceProvider>
  )
}

const TextWrapper = styled(Text)<{ color: keyof Colors }>`
  color: ${({ color, theme }) => (theme as any)[color]};
`

export const TYPE = {
  main(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text2'} {...props} />
  },
  link(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'primary1'} {...props} />
  },
  black(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text1'} {...props} />
  },
  body(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={16} color={'text1'} {...props} />
  },
  largeHeader(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={24} {...props} />
  },
  mediumHeader(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={20} {...props} />
  },
  subHeader(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={14} {...props} />
  },
  blue(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'primary1'} {...props} />
  },
  yellow(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'yellow1'} {...props} />
  },
  darkGray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text3'} {...props} />
  },
  gray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'bg3'} {...props} />
  },
  italic(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={12} fontStyle={'italic'} color={'text2'} {...props} />
  },
  error({ error, ...props }: { error: boolean } & TextProps) {
    return <TextWrapper fontWeight={500} color={error ? 'red1' : 'text2'} {...props} />
  }
}

export const FixedGlobalStyle = createGlobalStyle`
${fonts}

html, input, textarea, button {
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.011em;
  font-display: fallback;
}

html,
body {
  margin: 0;
  padding: 0;
}

* {
  box-sizing: border-box;
}

button {
  user-select: none;
}

html {
  font-size: 16px;
  font-variant: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}
`

export const ThemedGlobalStyle = createGlobalStyle`
html {
  color: ${({ theme }) => theme.text1};
  background-color: ${({ theme }) => theme.bg2};
}

#root {
  isolation: isolate;
  position: relative;
  min-height: 100%;
}

body {
  min-height: 100vh;
  background: transparent;
  position: relative;
}

*:focus-visible {
  outline: 2px solid ${({ theme }) => theme.focus.ring};
  outline-offset: 2px;
}

button:focus:not(:focus-visible),
[role='button']:focus:not(:focus-visible) {
  outline: none;
}
`
